import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Button from '@mulesoft/anypoint-components/lib/Button';
import { AuthenticationFieldsRouter as AuthenticationFields } from '../AuthenticationFields';
import AdvancedSettingsSection from '../AdvancedSettingsSection';
import {
  TARGET_SYSTEM_OPTIONS,
  ALL_AUTH_METHODS,
  TARGET_SYSTEM_AUTH_COMPATIBILITY
} from '../constants';
import styles from '../ScannerConfigurationForm.css';

/**
 * ConnectionSection Component
 *
 * Renders the connection configuration section with:
 * - Agent provider selector
 * - Authentication method selector
 * - Authentication credentials fields
 * - Connection test button and status
 * - Advanced settings (email notifications)
 */
const ConnectionSection = ({
  mode,
  formData,
  connectionStatus,
  connectionError,
  showAdvancedSettings,
  onChange,
  onTestConnection,
  onToggleAdvancedSettings,
  onToggleEmailNotifications,
  onAddEmail,
  onRemoveEmail
}) => {
  // Compute auth method options based on selected target system
  const authMethodOptions = useMemo(() => {
    const compatibleMethods = formData.targetSystem
      ? TARGET_SYSTEM_AUTH_COMPATIBILITY[formData.targetSystem] || []
      : [];

    return ALL_AUTH_METHODS.map((method) => ({
      ...method,
      disabled: formData.targetSystem
        ? !compatibleMethods.includes(method.value)
        : false
    }));
  }, [formData.targetSystem]);

  /**
   * Get target system display name for error messages.
   *
   * Edge case handling:
   * - null/undefined: .find() returns undefined, falls back to 'Unknown System'
   * - empty string '': No option matches, falls back to 'Unknown System'
   * - invalid value: No option matches, falls back to 'Unknown System'
   *
   * Note: In practice, the connection test button only appears when
   * formData.authMethod is set (line 116), which requires a valid
   * targetSystem selection first. The fallback primarily handles
   * unexpected states or data inconsistencies.
   */
  const targetSystemName = useMemo(() => {
    const targetSystem = TARGET_SYSTEM_OPTIONS.find(
      (option) => option.value === formData.targetSystem
    );

    return targetSystem?.label || 'Unknown System';
  }, [formData.targetSystem]);

  // Build default error message - kept generic to avoid tight coupling with field names
  const defaultErrorMessage = `We couldn't connect to ${targetSystemName}. Please verify your authentication credentials and try again.`;

  return (
    <section className={styles.section}>
      <div className={styles['section-title']}>Connection Configuration</div>

      <div className={styles.field}>
        <label htmlFor="target-system" className={styles.label}>
          Agent Provider <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="target-system"
            name="targetSystem"
            testId="target-system"
            value={formData.targetSystem}
            placeholder="Select..."
            options={TARGET_SYSTEM_OPTIONS}
            onChange={(event) =>
              onChange('targetSystem', event.itemsSelected?.value || '')
            }
            required
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="authentication-method" className={styles.label}>
          Authentication Method <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="authentication-method"
            name="authMethod"
            testId="authentication-method"
            value={formData.authMethod}
            placeholder="Select..."
            options={authMethodOptions}
            onChange={(event) =>
              onChange('authMethod', event.itemsSelected?.value || '')
            }
            required
            disabled={!formData.targetSystem}
          />
        </div>
      </div>

      {/* Authentication Credentials */}
      <AuthenticationFields
        mode={mode}
        targetSystem={formData.targetSystem}
        authMethod={formData.authMethod}
        formData={formData}
        onChange={onChange}
      />

      {/* Check Connection Status */}
      {formData.authMethod && (
        <div className={styles.field}>
          <span className={styles['label-text']}>Verify Authentication</span>
          <div className={styles['field-content']}>
            {connectionStatus === 'idle' && (
              <React.Fragment>
                <p className={styles['connection-hint']}>
                  Test the connection to verify authentication
                </p>
                <Button
                  type="button"
                  kind="primary"
                  onClick={onTestConnection}
                  data-test-id="test-connection-button"
                >
                  Test connection
                </Button>
              </React.Fragment>
            )}

            {connectionStatus === 'checking' && (
              <Button
                type="button"
                kind="primary"
                disabled
                data-test-id="test-connection-button"
              >
                Testing connection...
              </Button>
            )}

            {connectionStatus === 'success' && (
              <span className={styles['status-success']}>
                ✓ Connection verified successfully
              </span>
            )}
            {connectionStatus === 'error' && (
              <span className={styles['status-error']}>
                ✕ {defaultErrorMessage}
                {connectionError && ` ${connectionError}`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Advanced Settings */}
      <AdvancedSettingsSection
        showAdvancedSettings={showAdvancedSettings}
        onToggleAdvancedSettings={onToggleAdvancedSettings}
        emailNotificationsEnabled={formData.emailNotificationsEnabled}
        onToggleEmailNotifications={onToggleEmailNotifications}
        recipientEmails={formData.recipientEmails}
        onAddEmail={onAddEmail}
        onRemoveEmail={onRemoveEmail}
      />
    </section>
  );
};

ConnectionSection.propTypes = {
  /** Form mode: 'add' or 'edit' */
  mode: PropTypes.oneOf(['add', 'edit']),
  /** Form data object containing all form values */
  formData: PropTypes.shape({
    targetSystem: PropTypes.string,
    authMethod: PropTypes.string,
    emailNotificationsEnabled: PropTypes.bool,
    recipientEmails: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  /** Current connection test status */
  connectionStatus: PropTypes.oneOf(['idle', 'checking', 'success', 'error'])
    .isRequired,
  /** Connection test error message */
  connectionError: PropTypes.string,
  /** Whether advanced settings section is expanded */
  showAdvancedSettings: PropTypes.bool.isRequired,
  /** Handler for form field changes */
  onChange: PropTypes.func.isRequired,
  /** Handler for test connection button */
  onTestConnection: PropTypes.func.isRequired,
  /** Handler to toggle advanced settings visibility */
  onToggleAdvancedSettings: PropTypes.func.isRequired,
  /** Handler to toggle email notifications */
  onToggleEmailNotifications: PropTypes.func.isRequired,
  /** Handler to add a new email */
  onAddEmail: PropTypes.func.isRequired,
  /** Handler to remove an email */
  onRemoveEmail: PropTypes.func.isRequired
};

export default ConnectionSection;
